import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import { CircularProgress, Link, Typography } from '@mui/material';
import { clsx } from 'clsx';
import { format } from 'date-fns';
import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { generatePath, useNavigate } from 'react-router';

import { ConfirmModal } from '~/components/ConfirmModal';
import { EmptyData } from '~/components/EmptyData';
import { Pagination } from '~/components/Pagination';
import { ProgressBar } from '~/components/ProgressBar';
import { ROUTES } from '~/constants/routes';
import { useNotification } from '~/hooks/useNotification';
import { useDeleteDraftCompanyMutation } from '~/services/rtkQuery/company';
import { GetDraftCompaniesResponse } from '~/services/rtkQuery/company/types';
import { formatUrl } from '~/utils/formats';
import { getCurrentPageInfo, getTotalPagesCount } from '~/utils/pagination';

import styles from './styles.module.scss';

interface Props {
  fetchLimit: number;
  loading: boolean;
  currentPage: number;
  data: GetDraftCompaniesResponse['result'] | null;
  onPageChange: (page: number) => void;
}

const Component: React.FC<Props> = ({ fetchLimit, loading, currentPage, data, onPageChange }) => {
  const navigate = useNavigate();
  const [deleteConfirmModalVisible, setDeleteConfirmModalVisible] = useState(false);
  const { showNotification, handleApiError } = useNotification();
  const activeCompanyId = useRef<number | null>(null);
  const [deleteDraftCompanyMutation, { isLoading: isLoadingDeleteDraftCompanyMutation }] =
    useDeleteDraftCompanyMutation();

  const handleDraftCompanyClick = useCallback(
    (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>, id: number) => {
      // @ts-expect-error tagName exists
      if (event.target.tagName !== 'A') {
        navigate(generatePath(ROUTES.EDIT_COMPANY, { id: id.toString() }));
      }
    },
    [navigate],
  );

  const showDeleteConfirmModal = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, id: number) => {
      event.stopPropagation();

      activeCompanyId.current = id;

      setDeleteConfirmModalVisible(true);
    },
    [],
  );

  const hideDeleteConfirmModal = useCallback(() => {
    setDeleteConfirmModalVisible(false);
  }, []);

  const handleDraftCompanyDelete = useCallback(async () => {
    try {
      if (activeCompanyId.current) {
        const response = await deleteDraftCompanyMutation({ id: activeCompanyId.current }).unwrap();

        showNotification({
          type: 'success',
          title: response.title,
          message: response.message,
        });

        hideDeleteConfirmModal();
      }
    } catch (e) {
      handleApiError(e);
    }
  }, [deleteDraftCompanyMutation, handleApiError, hideDeleteConfirmModal, showNotification]);

  const draftTotalPages = useMemo(() => {
    return getTotalPagesCount({
      totalItemsCount: data?.total || 0,
      fetchLimit,
    });
  }, [data?.total, fetchLimit]);

  if (loading) {
    return (
      <div className={styles.loading}>
        <CircularProgress />
      </div>
    );
  }

  if (data?.data.length === 0) {
    return <EmptyData description="No companies available" title="Draft" />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.container__section}>
        <div className={styles['container__table-container']}>
          <table className={clsx(styles.container__table, styles['container__table--draft'])}>
            <thead>
              <tr>
                <th>
                  <Typography
                    color="var(--text-3-color)"
                    fontWeight="300"
                    lineHeight="16px"
                    variant="body1"
                  >
                    Company Name
                  </Typography>
                </th>
                <th>
                  <Typography
                    color="var(--text-3-color)"
                    fontWeight="300"
                    lineHeight="16px"
                    variant="body1"
                  >
                    Responsible
                  </Typography>
                </th>
                <th>
                  <Typography
                    color="var(--text-3-color)"
                    fontWeight="300"
                    lineHeight="16px"
                    variant="body1"
                  >
                    Updated
                  </Typography>
                </th>
                <th>
                  <Typography
                    color="var(--text-3-color)"
                    fontWeight="300"
                    lineHeight="16px"
                    variant="body1"
                  >
                    Website Link
                  </Typography>
                </th>
                <th>
                  <Typography
                    color="var(--text-3-color)"
                    fontWeight="300"
                    lineHeight="16px"
                    variant="body1"
                  >
                    Progress Bar
                  </Typography>
                </th>
                <th>
                  <Typography
                    color="var(--text-3-color)"
                    fontWeight="300"
                    lineHeight="16px"
                    variant="body1"
                  >
                    Status
                  </Typography>
                </th>
              </tr>
            </thead>
            <tbody>
              {data?.data.map(company => (
                <tr key={company.id} onClick={event => handleDraftCompanyClick(event, company.id)}>
                  <td>
                    <Typography
                      color="text.primary"
                      fontWeight="500"
                      lineHeight="16px"
                      variant="body1"
                    >
                      {company.name}
                    </Typography>
                  </td>
                  <td>
                    <Typography
                      color="text.primary"
                      fontWeight="500"
                      lineHeight="16px"
                      variant="body1"
                    >
                      {company.user ? `${company.user.fist_name} ${company.user.last_name}` : 'N/A'}
                    </Typography>
                  </td>
                  <td>
                    <Typography
                      color="text.primary"
                      fontWeight="500"
                      lineHeight="16px"
                      variant="body1"
                    >
                      {format(new Date(company.updated_at), 'MMM dd, yyyy')}
                    </Typography>
                  </td>
                  <td>
                    <Link
                      color="rgb(var(--main-2-color-rgb))"
                      fontWeight="500"
                      href={formatUrl(company.website_url)}
                      lineHeight="20px"
                      target="_blank"
                      variant="body1"
                    >
                      {company.website_url}
                    </Link>
                  </td>
                  <td aria-label="Progress Bar">
                    <ProgressBar
                      className={styles['container__progress-bar']}
                      label="Profile Completed"
                      value={+(company.progress_bar * 100).toFixed(0)}
                    />
                  </td>
                  <td>
                    <div>
                      <div
                        className={clsx(
                          styles['container__status-badge'],
                          styles['container__status-badge--draft'],
                        )}
                      >
                        <Typography color="var(--gray-2-color)" lineHeight="16px" variant="body1">
                          Draft
                        </Typography>
                      </div>
                      <button
                        aria-label="Delete"
                        title="Delete"
                        type="button"
                        onClick={event => showDeleteConfirmModal(event, company.id)}
                      >
                        <DeleteForeverOutlinedIcon
                          sx={{
                            color: 'var(--gray-6-color)',
                            ':hover': { color: 'var(--gray-2-color)' },
                          }}
                        />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className={styles.container__footer}>
          <Typography color="var(--gray-2-color)" lineHeight="14px" variant="subtitle2">
            {getCurrentPageInfo({
              fetchLimit,
              currentPage,
              currentPageItemsCount: data?.data.length || 0,
              totalItemsCount: data?.total || 0,
            })}
          </Typography>
          <Pagination
            currentPage={currentPage}
            totalPages={draftTotalPages}
            onChange={onPageChange}
          />
        </div>
      </div>
      {/* <div className={styles.container__section}>
        <Typography
          color="var(--text-2-color)"
          fontWeight="700"
          lineHeight="20px"
          mb="16px"
          mr="30px"
          variant="subtitle1"
        >
          Calculating
        </Typography>
        <table className={clsx(styles.container__table, styles['container__table--calculating'])}>
          <thead>
            <tr>
              <th>
                <Typography
                  color="var(--text-3-color)"
                  fontWeight="300"
                  lineHeight="16px"
                  variant="body1"
                >
                  Company Name
                </Typography>
              </th>
              <th>
                <Typography
                  color="var(--text-3-color)"
                  fontWeight="300"
                  lineHeight="16px"
                  variant="body1"
                >
                  Responsible
                </Typography>
              </th>
              <th>
                <Typography
                  color="var(--text-3-color)"
                  fontWeight="300"
                  lineHeight="16px"
                  variant="body1"
                >
                  Started
                </Typography>
              </th>
              <th>
                <Typography
                  color="var(--text-3-color)"
                  fontWeight="300"
                  lineHeight="16px"
                  variant="body1"
                >
                  Time Passed
                </Typography>
              </th>
              <th>
                <Typography
                  color="var(--text-3-color)"
                  fontWeight="300"
                  lineHeight="16px"
                  variant="body1"
                >
                  Progress Bar
                </Typography>
              </th>
              <th>
                <Typography
                  color="var(--text-3-color)"
                  fontWeight="300"
                  lineHeight="16px"
                  variant="body1"
                >
                  Status
                </Typography>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <Typography color="text.primary" fontWeight="500" lineHeight="16px" variant="body1">
                  ABA
                </Typography>
              </td>
              <td>
                <Typography color="text.primary" fontWeight="500" lineHeight="16px" variant="body1">
                  Pavel Yemen
                </Typography>
              </td>
              <td>
                <Typography color="text.primary" fontWeight="500" lineHeight="16px" variant="body1">
                  June 1, 2023 16:34 PM
                </Typography>
              </td>
              <td>
                <Typography color="text.primary" fontWeight="500" lineHeight="16px" variant="body1">
                  12 hours
                </Typography>
              </td>
              <td aria-label="Progress Bar">
                <ProgressBar
                  className={styles['container__progress-bar']}
                  label="Calculation Completed"
                  value={30}
                />
              </td>
              <td>
                <div className={clsx(styles.container__status, styles['container__status--draft'])}>
                  <Typography color="var(--yellow-1-color)" lineHeight="16px" variant="body1">
                    Calculating
                  </Typography>
                </div>
              </td>
            </tr>
            <tr>
              <td>
                <Typography color="text.primary" fontWeight="500" lineHeight="16px" variant="body1">
                  ABA
                </Typography>
              </td>
              <td>
                <Typography color="text.primary" fontWeight="500" lineHeight="16px" variant="body1">
                  Pavel Yemen
                </Typography>
              </td>
              <td>
                <Typography color="text.primary" fontWeight="500" lineHeight="16px" variant="body1">
                  June 1, 2023 16:34 PM
                </Typography>
              </td>
              <td>
                <Typography color="text.primary" fontWeight="500" lineHeight="16px" variant="body1">
                  12 hours
                </Typography>
              </td>
              <td aria-label="Progress Bar">
                <ProgressBar
                  className={styles['container__progress-bar']}
                  label="Calculation Completed"
                  value={30}
                />
              </td>
              <td>
                <div className={clsx(styles.container__status, styles['container__status--draft'])}>
                  <Typography color="var(--yellow-1-color)" lineHeight="16px" variant="body1">
                    Calculating
                  </Typography>
                </div>
              </td>
            </tr>
            <tr>
              <td>
                <Typography color="text.primary" fontWeight="500" lineHeight="16px" variant="body1">
                  ABA
                </Typography>
              </td>
              <td>
                <Typography color="text.primary" fontWeight="500" lineHeight="16px" variant="body1">
                  Pavel Yemen
                </Typography>
              </td>
              <td>
                <Typography color="text.primary" fontWeight="500" lineHeight="16px" variant="body1">
                  June 1, 2023 16:34 PM
                </Typography>
              </td>
              <td>
                <Typography color="text.primary" fontWeight="500" lineHeight="16px" variant="body1">
                  12 hours
                </Typography>
              </td>
              <td aria-label="Progress Bar">
                <ProgressBar
                  className={styles['container__progress-bar']}
                  label="Calculation Completed"
                  value={30}
                />
              </td>
              <td>
                <div className={clsx(styles.container__status, styles['container__status--draft'])}>
                  <Typography color="var(--yellow-1-color)" lineHeight="16px" variant="body1">
                    Calculating
                  </Typography>
                </div>
              </td>
            </tr>
            <tr>
              <td>
                <Typography color="text.primary" fontWeight="500" lineHeight="16px" variant="body1">
                  ABA
                </Typography>
              </td>
              <td>
                <Typography color="text.primary" fontWeight="500" lineHeight="16px" variant="body1">
                  Pavel Yemen
                </Typography>
              </td>
              <td>
                <Typography color="text.primary" fontWeight="500" lineHeight="16px" variant="body1">
                  June 1, 2023 16:34 PM
                </Typography>
              </td>
              <td>
                <Typography color="text.primary" fontWeight="500" lineHeight="16px" variant="body1">
                  12 hours
                </Typography>
              </td>
              <td aria-label="Progress Bar">
                <ProgressBar
                  className={styles['container__progress-bar']}
                  label="Calculation Completed"
                  value={30}
                />
              </td>
              <td>
                <div className={clsx(styles.container__status, styles['container__status--draft'])}>
                  <Typography color="var(--yellow-1-color)" lineHeight="16px" variant="body1">
                    Calculating
                  </Typography>
                </div>
              </td>
            </tr>
            <tr>
              <td>
                <Typography color="text.primary" fontWeight="500" lineHeight="16px" variant="body1">
                  ABA
                </Typography>
              </td>
              <td>
                <Typography color="text.primary" fontWeight="500" lineHeight="16px" variant="body1">
                  Pavel Yemen
                </Typography>
              </td>
              <td>
                <Typography color="text.primary" fontWeight="500" lineHeight="16px" variant="body1">
                  June 1, 2023 16:34 PM
                </Typography>
              </td>
              <td>
                <Typography color="text.primary" fontWeight="500" lineHeight="16px" variant="body1">
                  12 hours
                </Typography>
              </td>
              <td aria-label="Progress Bar">
                <ProgressBar
                  className={styles['container__progress-bar']}
                  label="Calculation Completed"
                  value={30}
                />
              </td>
              <td>
                <div className={clsx(styles.container__status, styles['container__status--draft'])}>
                  <Typography color="var(--yellow-1-color)" lineHeight="16px" variant="body1">
                    Calculating
                  </Typography>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <div className={styles.container__footer}>
          <Typography color="var(--gray-2-color)" lineHeight="14px" variant="subtitle2">
            Showing 10 from 50 data
          </Typography>
          <Pagination currentPage={1} totalPages={4} onChange={() => {}} />
        </div>
      </div> */}
      <ConfirmModal
        description="Are you sure you want to delete this company?"
        loading={isLoadingDeleteDraftCompanyMutation}
        open={deleteConfirmModalVisible}
        title="Do you want to delete?"
        onCancel={hideDeleteConfirmModal}
        onConfirm={handleDraftCompanyDelete}
      />
    </div>
  );
};

export const DraftDeals = memo(Component);
